import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Sparkles } from 'lucide-react';
import { inventoryPolicyControlDashboardMocks } from '../data/controlCenter.mock';
import { useInventoryPolicyControlScope } from '../hooks/useInventoryPolicyControlScope';
import type {
  ControlPriority,
  InventoryPolicyControlScope,
  PolicyProblemInsight,
  ProblemAffectedPolicyRow,
  ProblemPolicySuggestedChange,
} from '../model/controlCenter.types';

import { TablePaginationBar } from '@/components/shared/TablePaginationBar';
import { DEFAULT_TABLE_PAGE_SIZE } from '@/components/shared/tablePaginationConstants';

function priorityBadgeClass(priority: ControlPriority): string {
  switch (priority) {
    case 'alta':
      return 'bg-red-100 text-red-800';
    case 'media':
      return 'bg-amber-100 text-amber-800';
    case 'baja':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

interface DerivedAffectedPolicyMetrics {
  demandToLeadTime: number;
  salesVariabilitySafetyStock: number;
  supplierVariabilitySafetyStock: number;
  stockCoverageDays: number;
  fillRate: number | null;
  abcClass: string;
  storeAbcClass: string;
  daysWithoutStock: number;
  isSeasonal: boolean;
}

function deriveAffectedPolicyMetrics(
  policy: ProblemAffectedPolicyRow,
  problemCode: string,
  scope: InventoryPolicyControlScope,
  index: number
): DerivedAffectedPolicyMetrics {
  const baseDemandToLeadTime = Math.max(policy.reorderPoint - policy.safetyStock, 0);
  const demandToLeadTime = Math.min(
    policy.reorderPoint,
    Math.max(0, policy.demandToLeadTime ?? baseDemandToLeadTime)
  );

  const salesRatio =
    problemCode === 'PROV-003'
      ? 0.35
      : problemCode === 'ROP-001' || problemCode === 'SERV-001' || problemCode === 'SERV-002'
        ? 0.7
        : problemCode === 'OPS-001'
          ? 0.55
          : 0.6;

  const salesVariabilitySafetyStock =
    policy.salesVariabilitySafetyStock ?? Math.max(1, Math.round(policy.safetyStock * salesRatio));
  const supplierVariabilitySafetyStock =
    policy.supplierVariabilitySafetyStock ?? Math.max(0, policy.safetyStock - salesVariabilitySafetyStock);

  const dailyDemand = demandToLeadTime / Math.max(policy.leadTimeDays, 1);
  const stockCoverageDays =
    policy.stockCoverageDays ??
    Number((policy.currentStock / Math.max(dailyDemand, 0.1)).toFixed(1));

  const fillRate =
    policy.fillRate ??
    (scope === 'stores'
      ? Math.min(
          99.5,
          Math.max(
            82,
            ({
              'SERV-001': 86,
              'SERV-002': 91,
              'EXC-001': 97,
              'OPS-001': 93,
              'FREQ-004': 94,
            }[problemCode] ?? 94) + index * 0.6
          )
        )
      : null);

  const abcSequence = ['A', 'A', 'B', 'B', 'C'];
  const storeAbcSequence = ['A', 'B', 'A', 'B', 'C'];
  const abcClass = policy.abcClass ?? abcSequence[index % abcSequence.length];
  const storeAbcClass = policy.storeAbcClass ?? storeAbcSequence[index % storeAbcSequence.length];

  const daysWithoutStock =
    policy.daysWithoutStock ??
    (() => {
      switch (problemCode) {
        case 'SERV-001':
          return 2 + index;
        case 'ROP-001':
          return index < 2 ? 1 + index : 0;
        case 'OPS-001':
          return index % 2;
        default:
          return 0;
      }
    })();

  const isSeasonal =
    policy.isSeasonal ??
    (/iphone|gaming|watch|smartwatch|bluetooth|auricular|altavoz|premium/i.test(policy.productName) &&
      index % 2 === 0);

  return {
    demandToLeadTime,
    salesVariabilitySafetyStock,
    supplierVariabilitySafetyStock,
    stockCoverageDays,
    fillRate,
    abcClass,
    storeAbcClass,
    daysWithoutStock,
    isSeasonal,
  };
}

function formatSuggestedChangeValue(value: number | string, unit?: string): string {
  if (typeof value === 'number') {
    return `${value.toLocaleString('es-ES')}${unit ? ` ${unit}` : ''}`;
  }

  return String(value);
}

interface CategoryDistributionRow {
  label: string;
  count: number;
  percentage: number;
}

interface CategoryDistributionCard {
  id: string;
  title: string;
  rows: CategoryDistributionRow[];
}

function normalizePolicyText(value: string): string {
  return value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function inferPolicyFamily(policy: ProblemAffectedPolicyRow): string {
  if (policy.productFamily) return policy.productFamily;

  const name = normalizePolicyText(policy.productName);

  if (/freno|disco|pastilla/.test(name)) return 'Frenos';
  if (/cable|cargador|adaptador|protector|funda|hub|powerbank/.test(name)) return 'Accesorios moviles';
  if (/auricular|altavoz|audio|noise cancel/.test(name)) return 'Audio portatil';
  if (/gaming|teclado|mouse|raton/.test(name)) return 'Accesorios gaming';
  if (/watch|smartwatch|pulsera/.test(name)) return 'Wearables';
  if (/aluminio|acero|pletina|placa|escuadra|perfil/.test(name)) return 'Metal y perfileria';
  if (/componente|conector|terminal|tornillo|abrazadera/.test(name)) return 'Componentes';
  if (/llave|herramienta/.test(name)) return 'Herramientas';

  return 'Otras familias';
}

function inferLocationTypology(policy: ProblemAffectedPolicyRow, scope: InventoryPolicyControlScope): string {
  if (policy.locationTypology) return policy.locationTypology;

  const location = normalizePolicyText(policy.pointOfSale);

  if (scope === 'stores') {
    if (/gran via|goya|colon|larios/.test(location)) return 'Flagship urbana';
    if (/barcelona centro|sevilla centro|oviedo centro|santander centro|murcia centro/.test(location)) {
      return 'Tienda urbana';
    }

    return 'Tienda regional';
  }

  if (/central/.test(location)) return 'Almacen central';
  return 'Sucursal regional';
}

function inferXyzClass(
  policy: ProblemAffectedPolicyRow,
  problemCode: string,
  index: number,
  metrics: DerivedAffectedPolicyMetrics
): string {
  if (policy.xyzClass) return policy.xyzClass;

  if (problemCode === 'ROP-001' || problemCode === 'SERV-001') {
    return metrics.daysWithoutStock > 0 || index % 2 === 0 ? 'Z' : 'Y';
  }

  if (problemCode === 'SERV-002' || problemCode === 'OPS-001') {
    return index % 3 === 0 ? 'Z' : 'Y';
  }

  if (problemCode === 'SS-003' || problemCode === 'EXC-001') {
    return index % 2 === 0 ? 'X' : 'Y';
  }

  if (problemCode === 'PROV-003') {
    return 'Y';
  }

  return index % 2 === 0 ? 'X' : 'Y';
}

function buildDistributionRows(labels: string[], totalPolicies: number): CategoryDistributionRow[] {
  if (labels.length === 0 || totalPolicies === 0) return [];

  const sampleCounts = labels.reduce<Record<string, number>>((acc, label) => {
    acc[label] = (acc[label] ?? 0) + 1;
    return acc;
  }, {});

  const sampleTotal = labels.length;
  const projected = Object.entries(sampleCounts).map(([label, sampleCount]) => {
    const raw = (sampleCount / sampleTotal) * totalPolicies;
    const floor = Math.floor(raw);
    return {
      label,
      raw,
      count: floor,
      remainder: raw - floor,
      sampleCount,
    };
  });

  let assigned = projected.reduce((sum, row) => sum + row.count, 0);
  const remaining = Math.max(0, totalPolicies - assigned);

  projected
    .sort((a, b) => {
      if (b.remainder !== a.remainder) return b.remainder - a.remainder;
      return b.sampleCount - a.sampleCount;
    })
    .slice(0, remaining)
    .forEach((row) => {
      row.count += 1;
      assigned += 1;
    });

  return projected
    .filter((row) => row.count > 0)
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.label.localeCompare(b.label);
    })
    .map((row) => ({
      label: row.label,
      count: row.count,
      percentage: Math.round((row.count / totalPolicies) * 100),
    }));
}

type PolicyDecisionStatus = 'pending' | 'applied' | 'rejected';

interface PolicyDecisionState {
  draftValue: string;
  status: PolicyDecisionStatus;
}

function getSuggestedDraftValue(change?: ProblemPolicySuggestedChange): string {
  if (!change) return '';
  return typeof change.suggestedValue === 'number'
    ? String(change.suggestedValue)
    : String(change.suggestedValue);
}

function decisionBadgeClass(status: PolicyDecisionStatus): string {
  switch (status) {
    case 'applied':
      return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
    case 'rejected':
      return 'bg-rose-50 text-rose-700 border border-rose-200';
    case 'pending':
    default:
      return 'bg-amber-50 text-amber-700 border border-amber-200';
  }
}

export function ProblemInsightsPage() {
  const { t } = useTranslation();
  const { scope } = useInventoryPolicyControlScope();
  const data = inventoryPolicyControlDashboardMocks[scope];
  const [problemPage, setProblemPage] = useState(1);
  const [problemPageSize, setProblemPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [policiesPage, setPoliciesPage] = useState(1);
  const [policiesPageSize, setPoliciesPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [selectedProblemId, setSelectedProblemId] = useState<string>(data.problemInsights[0]?.id ?? '');
  const [policyDecisions, setPolicyDecisions] = useState<Record<string, PolicyDecisionState>>({});

  const totalProblemPages = Math.max(1, Math.ceil(data.problemInsights.length / problemPageSize));
  const paginatedProblems = useMemo(() => {
    const start = (problemPage - 1) * problemPageSize;
    return data.problemInsights.slice(start, start + problemPageSize);
  }, [data.problemInsights, problemPage, problemPageSize]);

  useEffect(() => {
    setProblemPage(1);
    setSelectedProblemId(data.problemInsights[0]?.id ?? '');
  }, [data.problemInsights]);

  useEffect(() => {
    if (problemPage > totalProblemPages) {
      setProblemPage(totalProblemPages);
    }
  }, [problemPage, totalProblemPages]);

  useEffect(() => {
    if (paginatedProblems.length === 0) {
      setSelectedProblemId('');
      return;
    }

    const selectedExistsInPage = paginatedProblems.some((problem) => problem.id === selectedProblemId);
    if (!selectedExistsInPage) {
      setSelectedProblemId(paginatedProblems[0].id);
    }
  }, [paginatedProblems, selectedProblemId]);

  const selectedProblem: PolicyProblemInsight | null =
    data.problemInsights.find((problem) => problem.id === selectedProblemId) ??
    paginatedProblems[0] ??
    null;

  const sourceColumnKey =
    scope === 'stores'
      ? 'inventoryPolicyControl.problemInsights.policies.sourceWarehouse'
      : 'inventoryPolicyControl.problemInsights.policies.supplier';
  const primaryCause = selectedProblem?.primaryCause ?? '-';
  const secondaryCauses = selectedProblem?.secondaryCauses ?? [];

  const categoryDistributions = useMemo<CategoryDistributionCard[]>(() => {
    if (!selectedProblem) return [];

    const policyContexts = selectedProblem.affectedPolicies.map((policy, index) => {
      const metrics = deriveAffectedPolicyMetrics(policy, selectedProblem.problemCode, scope, index);
      const xyzClass = inferXyzClass(policy, selectedProblem.problemCode, index, metrics);

      return {
        family: inferPolicyFamily(policy),
        locationTypology: inferLocationTypology(policy, scope),
        supplier: policy.supplier,
        abcXyz: `${metrics.abcClass} / ${xyzClass}`,
      };
    });

    return [
      {
        id: 'family',
        title: t('inventoryPolicyControl.problemInsights.homologated.familyProduct'),
        rows: buildDistributionRows(
          policyContexts.map((policy) => policy.family),
          selectedProblem.affectedPoliciesCount
        ),
      },
      {
        id: 'storeTypology',
        title: t('inventoryPolicyControl.problemInsights.homologated.storeTypology'),
        rows: buildDistributionRows(
          policyContexts.map((policy) => policy.locationTypology),
          selectedProblem.affectedPoliciesCount
        ),
      },
      {
        id: 'supplier',
        title:
          scope === 'stores'
            ? t('inventoryPolicyControl.problemInsights.homologated.sourceOrigin')
            : t('inventoryPolicyControl.problemInsights.homologated.supplier'),
        rows: buildDistributionRows(
          policyContexts.map((policy) => policy.supplier),
          selectedProblem.affectedPoliciesCount
        ),
      },
      {
        id: 'abcXyz',
        title: t('inventoryPolicyControl.problemInsights.homologated.abcXyz'),
        rows: buildDistributionRows(
          policyContexts.map((policy) => policy.abcXyz),
          selectedProblem.affectedPoliciesCount
        ),
      },
    ];
  }, [scope, selectedProblem, t]);

  const totalPoliciesPages = Math.max(
    1,
    Math.ceil((selectedProblem?.affectedPolicies.length ?? 0) / policiesPageSize)
  );
  const paginatedPolicies = useMemo(() => {
    if (!selectedProblem) return [];
    const start = (policiesPage - 1) * policiesPageSize;
    return selectedProblem.affectedPolicies.slice(start, start + policiesPageSize);
  }, [policiesPage, policiesPageSize, selectedProblem]);

  useEffect(() => {
    setPoliciesPage(1);
  }, [selectedProblem?.id]);

  useEffect(() => {
    if (!selectedProblem) {
      setPolicyDecisions({});
      return;
    }

    setPolicyDecisions(
      Object.fromEntries(
        selectedProblem.affectedPolicies.map((policy) => [
          policy.id,
          {
            draftValue: getSuggestedDraftValue(policy.suggestedChange),
            status: 'pending' as PolicyDecisionStatus,
          },
        ])
      )
    );
  }, [selectedProblem]);

  useEffect(() => {
    if (policiesPage > totalPoliciesPages) {
      setPoliciesPage(totalPoliciesPages);
    }
  }, [policiesPage, totalPoliciesPages]);

  const updatePolicyDraft = (policyId: string, draftValue: string) => {
    setPolicyDecisions((prev) => ({
      ...prev,
      [policyId]: {
        draftValue,
        status: 'pending',
      },
    }));
  };

  const setPolicyStatus = (policyId: string, status: PolicyDecisionStatus) => {
    setPolicyDecisions((prev) => ({
      ...prev,
      [policyId]: {
        draftValue: prev[policyId]?.draftValue ?? '',
        status,
      },
    }));
  };

  const applyAllPolicies = () => {
    if (!selectedProblem) return;

    setPolicyDecisions((prev) =>
      Object.fromEntries(
        selectedProblem.affectedPolicies.map((policy) => {
          const currentDraftValue = prev[policy.id]?.draftValue ?? getSuggestedDraftValue(policy.suggestedChange);
          const nextStatus =
            policy.suggestedChange && currentDraftValue.trim().length > 0
              ? ('applied' as PolicyDecisionStatus)
              : (prev[policy.id]?.status ?? 'pending');

          return [
            policy.id,
            {
              draftValue: currentDraftValue,
              status: nextStatus,
            },
          ];
        })
      )
    );
  };

  const editablePoliciesCount =
    selectedProblem?.affectedPolicies.filter((policy) => policy.suggestedChange != null).length ?? 0;

  return (
    <div className="p-6 pb-16 space-y-6 min-h-full">
      <section className="bg-white rounded-lg shadow-sm p-4 border border-gray-200 space-y-4">
        <div className="rounded-lg bg-blue-50 px-4 py-2.5 text-sm text-blue-800 flex items-center gap-2">
          <Sparkles className="h-4 w-4" aria-hidden />
          {t('inventoryPolicyControl.problemInsights.helper')}
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  {t('inventoryPolicyControl.problemInsights.table.problem')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  {t('inventoryPolicyControl.problemInsights.table.affectedPolicies')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  {t('inventoryPolicyControl.problemInsights.table.impact')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  {t('inventoryPolicyControl.problemInsights.table.priority')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {paginatedProblems.map((problem) => {
                const isSelected = selectedProblem?.id === problem.id;
                return (
                  <tr
                    key={problem.id}
                    onClick={() => setSelectedProblemId(problem.id)}
                    className={`cursor-pointer transition-colors ${
                      isSelected ? 'bg-blue-50/70' : 'hover:bg-gray-50'
                    }`}
                  >
                    <td className="px-6 py-4 text-gray-900 font-medium">{problem.problemLabel}</td>
                    <td className="px-6 py-4 text-gray-900">{problem.affectedPoliciesCount.toLocaleString('es-ES')}</td>
                    <td className="px-6 py-4 text-gray-900">{problem.estimatedImpactLabel}</td>
                    <td className="px-6 py-4">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${priorityBadgeClass(problem.priority)}`}>
                        {t(`inventoryPolicyControl.priority.${problem.priority}`)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <TablePaginationBar
          currentPage={problemPage}
          totalPages={totalProblemPages}
          totalItems={data.problemInsights.length}
          pageSize={problemPageSize}
          onPageChange={setProblemPage}
          onPageSizeChange={(size) => {
            setProblemPageSize(size);
            setProblemPage(1);
          }}
        />
      </section>

      {!selectedProblem ? (
        <section className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 text-sm text-gray-500">
          {t('inventoryPolicyControl.problemInsights.empty')}
        </section>
      ) : (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-[1.15fr_0.85fr] gap-6">
            <section className="bg-white rounded-lg shadow-sm p-5 border border-gray-200 space-y-5">
              <div>
                <p className="text-sm text-[#1681ba] font-medium mb-2">
                  {t('inventoryPolicyControl.problemInsights.detailLabel')}
                </p>
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">{selectedProblem.problemLabel}</h2>
                    <p className="text-sm text-gray-500 mt-2">{selectedProblem.summary}</p>
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full h-fit ${priorityBadgeClass(selectedProblem.priority)}`}>
                    {t(`inventoryPolicyControl.priority.${selectedProblem.priority}`)}
                  </span>
                </div>
              </div>

              <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">
                  {t('inventoryPolicyControl.problemInsights.nextAction')}
                </h3>
                <p className="text-sm text-blue-900">{selectedProblem.recommendedAction}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-lg bg-gray-50 p-4">
                  <p className="text-xs text-gray-500">{t('inventoryPolicyControl.problemInsights.summary.affectedPolicies')}</p>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {selectedProblem.affectedPoliciesCount.toLocaleString('es-ES')}
                  </p>
                </div>
                <div className="rounded-lg bg-gray-50 p-4">
                  <p className="text-xs text-gray-500">{t('inventoryPolicyControl.problemInsights.summary.estimatedImpact')}</p>
                  <p className="text-lg font-semibold text-gray-900 mt-1">{selectedProblem.estimatedImpactLabel}</p>
                </div>
                <div className="rounded-lg bg-gray-50 p-4">
                  <p className="text-xs text-gray-500">{t('inventoryPolicyControl.problemInsights.summary.simulation')}</p>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {selectedProblem.simulationPossible
                      ? t('inventoryPolicyControl.problemInsights.summary.available')
                      : t('inventoryPolicyControl.problemInsights.summary.notAvailable')}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-lg border border-gray-100 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">
                    {t('inventoryPolicyControl.problemInsights.detectionBase')}
                  </h3>
                  <p className="text-sm text-gray-700">{selectedProblem.detectionCriteria}</p>
                </div>

                <div className="rounded-lg border border-gray-100 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">
                    {t('inventoryPolicyControl.problemInsights.primaryCause')}
                  </h3>
                  <p className="text-sm text-gray-700">{primaryCause}</p>
                </div>
              </div>

              {secondaryCauses.length > 0 ? (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">
                    {t('inventoryPolicyControl.problemInsights.reviewFactors')}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {secondaryCauses.map((cause) => (
                      <span
                        key={cause}
                        className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs text-amber-800"
                      >
                        <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                        {cause}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </section>

            <aside className="bg-white rounded-lg shadow-sm p-5 border border-gray-200 space-y-5">
              <section>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">
                  {t('inventoryPolicyControl.problemInsights.concentrationTitle')}
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  {t('inventoryPolicyControl.problemInsights.homologated.helper')}
                </p>
                <div className="grid grid-cols-1 gap-3">
                  {categoryDistributions.map((category) => (
                    <div key={category.id} className="rounded-lg border border-gray-100 px-3 py-3">
                      <p className="text-xs text-gray-500">{category.title}</p>
                      <div className="mt-3 space-y-3">
                        {category.rows.length > 0 ? (
                          category.rows.map((row) => (
                            <div key={`${category.id}-${row.label}`} className="space-y-1">
                              <div className="flex items-center justify-between gap-3">
                                <p className="text-sm font-medium text-gray-900">{row.label}</p>
                                <span className="text-xs text-gray-500">
                                  {row.count} {t('inventoryPolicyControl.problemInsights.homologated.policiesCount')} · {row.percentage}%
                                </span>
                              </div>
                              <div className="h-2 rounded-full bg-gray-100">
                                <div
                                  className="h-2 rounded-full bg-[#1681ba]"
                                  style={{ width: `${Math.min(100, row.percentage)}%` }}
                                />
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500">
                            {t('inventoryPolicyControl.problemInsights.homologated.noData')}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </aside>
          </div>

          <section className="bg-white rounded-lg shadow-sm p-5 pb-8 border border-gray-200">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('inventoryPolicyControl.problemInsights.affectedPoliciesTitle')}
              </h2>
              <button
                type="button"
                onClick={applyAllPolicies}
                disabled={editablePoliciesCount === 0}
                className="inline-flex items-center justify-center rounded-md bg-[#1681ba] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#1472a6] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('inventoryPolicyControl.problemInsights.policies.applyAll', {
                  count: editablePoliciesCount,
                })}
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              {selectedProblem.affectedProductsCount != null && selectedProblem.affectedLocationsCount != null
                ? t('inventoryPolicyControl.problemInsights.policies.previewSummaryWithBreakdown', {
                    shown: paginatedPolicies.length,
                    total: selectedProblem.affectedPoliciesCount,
                    products: selectedProblem.affectedProductsCount,
                    locations: selectedProblem.affectedLocationsCount,
                  })
                : t('inventoryPolicyControl.problemInsights.policies.previewSummary', {
                    shown: paginatedPolicies.length,
                    total: selectedProblem.affectedPoliciesCount,
                  })}
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-500">
                    <th className="py-2 pr-4 font-medium">{t('inventoryPolicyControl.problemInsights.policies.product')}</th>
                    <th className="py-2 pr-4 font-medium">{t('inventoryPolicyControl.problemInsights.policies.locationContext')}</th>
                    <th className="py-2 pr-4 font-medium">{t('inventoryPolicyControl.problemInsights.policies.inventoryStatus')}</th>
                    <th className="py-2 pr-4 font-medium">{t('inventoryPolicyControl.problemInsights.policies.policyParameters')}</th>
                    <th className="py-2 pr-4 font-medium">{t('inventoryPolicyControl.problemInsights.policies.safetyStockSplit')}</th>
                    <th className="py-2 pr-4 font-medium">{t('inventoryPolicyControl.problemInsights.policies.fillRate')}</th>
                    <th className="py-2 pr-4 font-medium">{t('inventoryPolicyControl.problemInsights.policies.classification')}</th>
                    <th className="py-2 font-medium">{t('inventoryPolicyControl.problemInsights.policies.suggestedAction')}</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedPolicies.map((policy, index) => {
                    const metrics = deriveAffectedPolicyMetrics(
                      policy,
                      selectedProblem.problemCode,
                      scope,
                      (policiesPage - 1) * policiesPageSize + index
                    );
                    const suggestedChange: ProblemPolicySuggestedChange | undefined = policy.suggestedChange;
                    const decision = policyDecisions[policy.id];
                    const draftValue = decision?.draftValue ?? getSuggestedDraftValue(suggestedChange);
                    const status = decision?.status ?? 'pending';
                    const isAdjusted =
                      suggestedChange != null && draftValue.trim() !== getSuggestedDraftValue(suggestedChange).trim();

                    return (
                    <tr key={policy.id} className="border-b border-gray-100 last:border-0 align-top">
                      <td className="py-3 pr-4 text-gray-900 min-w-[220px]">
                        <div className="flex flex-col">
                          <span className="font-medium">{policy.productName}</span>
                          <span className="text-xs text-gray-500">{policy.productCode}</span>
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-gray-900 min-w-[190px]">
                        <div className="space-y-1">
                          <div>
                            <p className="text-xs text-gray-500">{t('inventoryPolicyControl.problemInsights.policies.pointOfSale')}</p>
                            <p className="font-medium text-gray-900">{policy.pointOfSale}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">{t(sourceColumnKey)}</p>
                            <p className="text-gray-900">{policy.supplier}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-gray-900 min-w-[180px]">
                        <div className="space-y-1">
                          <div className="flex items-baseline justify-between gap-3">
                            <span className="text-xs text-gray-500">{t('inventoryPolicyControl.problemInsights.policies.currentStock')}</span>
                            <span className="font-medium">{policy.currentStock.toLocaleString('es-ES')}</span>
                          </div>
                          <div className="flex items-baseline justify-between gap-3">
                            <span className="text-xs text-gray-500">{t('inventoryPolicyControl.problemInsights.policies.coverageDays')}</span>
                            <span>{metrics.stockCoverageDays.toLocaleString('es-ES', { maximumFractionDigits: 1 })} {t('inventoryPolicyControl.problemInsights.policies.days')}</span>
                          </div>
                          <div className="flex items-baseline justify-between gap-3">
                            <span className="text-xs text-gray-500">{t('inventoryPolicyControl.problemInsights.policies.daysWithoutStock')}</span>
                            <span>{metrics.daysWithoutStock.toLocaleString('es-ES')}</span>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-gray-900 min-w-[190px]">
                        <div className="space-y-1">
                          <div className="flex items-baseline justify-between gap-3">
                            <span className="text-xs text-gray-500">{t('inventoryPolicyControl.problemInsights.policies.rop')}</span>
                            <span className="font-medium">{policy.reorderPoint.toLocaleString('es-ES')}</span>
                          </div>
                          <div className="flex items-baseline justify-between gap-3">
                            <span className="text-xs text-gray-500">{t('inventoryPolicyControl.problemInsights.policies.demandToLeadTime')}</span>
                            <span>{metrics.demandToLeadTime.toLocaleString('es-ES')}</span>
                          </div>
                          <div className="flex items-baseline justify-between gap-3">
                            <span className="text-xs text-gray-500">{t('inventoryPolicyControl.problemInsights.policies.leadTime')}</span>
                            <span>{policy.leadTimeDays} {t('inventoryPolicyControl.problemInsights.policies.days')}</span>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-gray-900 min-w-[190px]">
                        <div className="space-y-1">
                          <div className="flex items-baseline justify-between gap-3">
                            <span className="text-xs text-gray-500">{t('inventoryPolicyControl.problemInsights.policies.salesVariability')}</span>
                            <span>{metrics.salesVariabilitySafetyStock.toLocaleString('es-ES')}</span>
                          </div>
                          <div className="flex items-baseline justify-between gap-3">
                            <span className="text-xs text-gray-500">{t('inventoryPolicyControl.problemInsights.policies.supplierVariability')}</span>
                            <span>{metrics.supplierVariabilitySafetyStock.toLocaleString('es-ES')}</span>
                          </div>
                          <div className="flex items-baseline justify-between gap-3">
                            <span className="text-xs text-gray-500">{t('inventoryPolicyControl.problemInsights.policies.safetyStock')}</span>
                            <span className="font-medium">{policy.safetyStock.toLocaleString('es-ES')}</span>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-gray-900 min-w-[120px]">
                        {metrics.fillRate != null ? (
                          <div className="inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-sm font-medium text-emerald-700">
                            {metrics.fillRate.toLocaleString('es-ES', { maximumFractionDigits: 1 })}%
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">{t('inventoryPolicyControl.problemInsights.policies.notApplicable')}</span>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-gray-900 min-w-[180px]">
                        <div className="space-y-2">
                          <div className="flex flex-wrap gap-2">
                            <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
                              {t('inventoryPolicyControl.problemInsights.policies.abc')}: {metrics.abcClass}
                            </span>
                            <span className="rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700">
                              {t('inventoryPolicyControl.problemInsights.policies.storeAbc')}: {metrics.storeAbcClass}
                            </span>
                          </div>
                          <span
                            className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                              metrics.isSeasonal
                                ? 'bg-amber-50 text-amber-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {metrics.isSeasonal
                              ? t('inventoryPolicyControl.problemInsights.policies.possibleSeasonal')
                              : t('inventoryPolicyControl.problemInsights.policies.notSeasonal')}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 text-gray-900 min-w-[220px]">
                        {suggestedChange ? (
                          <div className="space-y-2 rounded-lg border border-gray-100 bg-gray-50 p-3">
                            <div className="flex items-start justify-between gap-3">
                              <p className="font-medium text-gray-900">
                                {t(
                                  `inventoryPolicyControl.problemInsights.policies.adjustmentFields.${suggestedChange.field}`
                                )}
                              </p>
                              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${decisionBadgeClass(status)}`}>
                                {t(`inventoryPolicyControl.problemInsights.policies.decisionStatus.${status}`)}
                              </span>
                            </div>

                            <div className="text-xs text-gray-500">
                              <span>{t('inventoryPolicyControl.problemInsights.policies.currentValue')}: </span>
                              <span>{formatSuggestedChangeValue(suggestedChange.currentValue, suggestedChange.unit)}</span>
                            </div>

                            <div>
                              <p className="text-xs text-gray-500 mb-1">
                                {t('inventoryPolicyControl.problemInsights.policies.suggestedValue')}
                              </p>
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={draftValue}
                                  onChange={(event) => updatePolicyDraft(policy.id, event.target.value)}
                                  className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-[#1681ba] focus:outline-none focus:ring-2 focus:ring-[#1681ba]/20"
                                />
                                {suggestedChange.unit ? (
                                  <span className="shrink-0 text-xs text-gray-500">{suggestedChange.unit}</span>
                                ) : null}
                              </div>
                            </div>

                            {isAdjusted ? (
                              <p className="text-xs font-medium text-amber-700">
                                {t('inventoryPolicyControl.problemInsights.policies.adjustedBeforeApply')}
                              </p>
                            ) : null}

                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => setPolicyStatus(policy.id, 'applied')}
                                disabled={draftValue.trim().length === 0}
                                className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                {t('inventoryPolicyControl.problemInsights.policies.apply')}
                              </button>
                              <button
                                type="button"
                                onClick={() => setPolicyStatus(policy.id, 'rejected')}
                                className="rounded-md border border-rose-200 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 transition-colors hover:bg-rose-50"
                              >
                                {t('inventoryPolicyControl.problemInsights.policies.reject')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          policy.suggestedAction
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <TablePaginationBar
              currentPage={policiesPage}
              totalPages={totalPoliciesPages}
              totalItems={selectedProblem?.affectedPolicies.length ?? 0}
              pageSize={policiesPageSize}
              onPageChange={setPoliciesPage}
              onPageSizeChange={(size) => {
                setPoliciesPageSize(size);
                setPoliciesPage(1);
              }}
            />
          </section>
        </>
      )}
    </div>
  );
}
